import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';

interface BaseSelectOption {
  value: string;
  label: string;
}

interface BaseSelectProps {
  value: string;
  options: BaseSelectOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  size?: 'sm' | 'md';
}

export default function BaseSelect({
  value,
  options,
  onChange,
  placeholder = 'Selecteer...',
  disabled = false,
  className = '',
  size = 'md',
}: BaseSelectProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = options.find(o => o.value === value);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleSelect = useCallback((val: string) => {
    onChange(val);
    setOpen(false);
  }, [onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen(prev => !prev);
    } else if (e.key === 'Escape' && open) {
      setOpen(false);
    }
  }, [open]);

  const px = size === 'sm' ? 'px-2.5' : 'px-3';
  const py = size === 'sm' ? 'py-1.5' : 'py-2.5';
  const textSize = size === 'sm' ? 'text-xs' : 'text-sm';

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => !disabled && setOpen(prev => !prev)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between rounded-lg
          border outline-none transition-all cursor-pointer
          ${px} ${py} ${textSize}
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          ${open ? 'border-[var(--border-active)]' : 'border-[var(--border-primary)]'}
        `}
        style={{
          background: 'var(--bg-input)',
          color: selected ? 'var(--text-primary)' : 'var(--text-muted)',
        }}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="truncate pr-2">
          {selected ? selected.label : placeholder}
        </span>
        <span
          className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          style={{ color: 'var(--text-muted)' }}
        >
          <ChevronDown size={size === 'sm' ? 14 : 16} />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 w-full mt-1 rounded-lg border overflow-hidden animate-dropdown"
          style={{
            background: 'var(--bg-surface-raised)',
            borderColor: 'var(--border-primary)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
          }}
          role="listbox"
        >
          <div className="max-h-56 overflow-y-auto py-1">
            {options.map(option => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`
                  w-full flex items-center justify-between
                  ${px} ${py} ${textSize}
                  text-left transition-colors cursor-pointer
                  hover:bg-[var(--bg-input)]
                `}
                style={{
                  color: value === option.value ? 'var(--text-primary)' : 'var(--text-secondary)',
                }}
                role="option"
                aria-selected={value === option.value}
              >
                <span className="truncate pr-2">{option.label}</span>
                {value === option.value && (
                  <Check size={14} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}